import type { Block } from './types';
import { newBlockId } from './blockId';

/**
 * Normalise blocks to the canonical `{ id, type, data }` shape. Ported from Scilora
 * (`lib/normalize-blocks.ts`): tolerates the legacy `{ type, attrs, content }` form
 * that early generators produced, so imported content never crashes the editor.
 */
export function normalizeBlocks(blocks: Block[]): Block[] {
    return blocks.map(normalizeBlock);
}

function normalizeBlock(block: Block): Block {
    if (block.data && Object.keys(block.data).length > 0) {
        return block;
    }

    const raw = block as unknown as { attrs?: Record<string, unknown>; content?: unknown };
    const attrs = raw.attrs ?? {};
    const content = raw.content;

    return {
        id: block.id || newBlockId(),
        type: block.type,
        data: mapLegacyData(block.type, attrs, content),
        meta: block.meta,
    };
}

function mapLegacyData(type: string, attrs: Record<string, unknown>, content: unknown): Record<string, unknown> {
    switch (type) {
        case 'heading':
            return { text: content, level: attrs.level ?? 2 };
        case 'richtext':
            return { html: content };
        case 'callout':
            return { html: content, variant: attrs.variant ?? 'info' };
        case 'image':
            return { src: attrs.src ?? '', alt: attrs.alt ?? '', caption: attrs.caption ?? '' };
        case 'code':
            return { code: content, language: attrs.language ?? 'text' };
        case 'divider':
            return {};
        case 'inline_quiz':
        case 'matching':
        case 'ordering':
            return attrs;
        case 'fill_blank':
            return convertFillBlank(attrs);
        case 'accordion':
            return {
                items: ((attrs.items as { title: string; content: string }[]) ?? []).map((item) => ({
                    title: item.title,
                    blocks: [{ id: newBlockId(), type: 'richtext', data: { html: `<p>${item.content}</p>` } }],
                    defaultOpen: false,
                })),
                allowMultiple: false,
            };
        case 'columns':
            return {
                columns: (Array.isArray(content) ? content : []).map((nested: Record<string, unknown>) => ({
                    blocks: [normalizeBlock(nested as unknown as Block)],
                })),
            };
        case 'tabs':
            return {
                tabs: ((attrs.tabs as { title?: string; label?: string; content: string }[]) ?? []).map((tab) => ({
                    label: tab.title ?? tab.label ?? '',
                    blocks: [{ id: newBlockId(), type: 'richtext', data: { html: `<p>${tab.content}</p>` } }],
                })),
            };
        default:
            return { ...attrs, ...(content != null ? { content } : {}) };
    }
}

function convertFillBlank(attrs: Record<string, unknown>): Record<string, unknown> {
    if (attrs.template) return attrs;

    const text = (attrs.text as string) ?? '';
    const blanks: { id: string; acceptedAnswers: string[] }[] = [];
    let blankIndex = 0;

    const template = text.replace(/\{\{(.+?)\}\}/g, (_match, answer: string) => {
        const id = `blank_${blankIndex++}`;
        blanks.push({ id, acceptedAnswers: [answer.trim()] });
        return `{{${id}}}`;
    });

    return { template, blanks };
}
